import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Button, Card, CardContent, CardHeader, Spinner } from "@learn-me/ui";
import { useCourseById } from "@/features/courses/hooks/useCourses";
import { api } from "@/lib/api";
import { formatCurrency } from "@learn-me/utils";
import {
  ArrowLeft,
  BookOpen,
  Shield,
  Check,
  CreditCard,
  Gift,
} from "lucide-react";
import toast from "react-hot-toast";
import type { ApiResponse } from "@learn-me/types";
import { normalizeImageUrl } from "@/lib/imageUrl";
import TermsCheckbox from "@/components/legal/TermsCheckbox";
import CertificationDisclaimer from "@/components/legal/CertificationDisclaimer";
import {
  LEGAL_TERMS_VERSION,
  LEGAL_PRIVACY_VERSION,
} from "@/content/legal/versions";

const PAYPAL_CLIENT_ID = import.meta.env.VITE_PAYPAL_CLIENT_ID || "";

export default function CheckoutPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { data: course, isLoading } = useCourseById(courseId!);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const createOrderMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<
        ApiResponse<{ orderId: string; approvalUrl: string }>
      >("/payments/paypal/create-order", {
        courseId,
        termsAcceptance: {
          termsVersion: LEGAL_TERMS_VERSION,
          privacyVersion: LEGAL_PRIVACY_VERSION,
          contextPath: "/checkout",
        },
      });
      return response.data.data!;
    },
  });

  const captureOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const response = await api.post<
        ApiResponse<{ enrollment: unknown; payment?: { id: string } }>
      >("/payments/paypal/capture-order", { orderId });
      return response.data.data!;
    },
    onSuccess: (data) => {
      toast.success("¡Compra realizada con éxito!");
      navigate("/checkout/success", {
        state: {
          courseId: courseId,
          courseSlug: course?.slug,
          orderId: (data as { payment?: { id: string } })?.payment?.id,
        },
      });
    },
    onError: () => {
      toast.error("Error al procesar el pago. Intenta de nuevo.");
    },
  });

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-2xl font-bold mb-2">Curso no encontrado</h1>
        <Link to="/courses">
          <Button>Ver cursos</Button>
        </Link>
      </div>
    );
  }

  if (course.isFree) {
    navigate(`/courses/${course.slug}`);
    return null;
  }

  // Step indicator for checkout process
  const isProcessing =
    createOrderMutation.isPending || captureOrderMutation.isPending;

  return (
    <div className="py-8">
      <div className="container max-w-4xl">
        <Link
          to={`/courses/${course.slug}`}
          className="inline-flex items-center text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver al curso
        </Link>

        {/* Checkout Steps Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-center">
            {/* Step 1: Order Summary */}
            <div className="flex items-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white">
                <Check className="h-5 w-5" />
              </div>
              <span className="ml-2 text-sm font-medium hidden sm:block">
                Resumen
              </span>
            </div>

            {/* Connector */}
            <div className="w-12 sm:w-24 h-1 mx-2 bg-primary-600" />

            {/* Step 2: Payment */}
            <div className="flex items-center">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full ${
                  isProcessing
                    ? "bg-primary-600 text-white animate-pulse"
                    : "bg-primary-600 text-white"
                }`}
              >
                <CreditCard className="h-5 w-5" />
              </div>
              <span className="ml-2 text-sm font-medium hidden sm:block">
                {isProcessing ? "Procesando..." : "Pago"}
              </span>
            </div>

            {/* Connector */}
            <div
              className={`w-12 sm:w-24 h-1 mx-2 ${isProcessing ? "bg-muted" : "bg-muted"}`}
            />

            {/* Step 3: Confirmation */}
            <div className="flex items-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted text-muted-foreground">
                <Gift className="h-5 w-5" />
              </div>
              <span className="ml-2 text-sm font-medium text-muted-foreground hidden sm:block">
                Confirmación
              </span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Resumen del Pedido</h2>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-6">
                <div className="w-24 h-16 bg-muted rounded overflow-hidden flex-shrink-0">
                  {course.thumbnailUrl ? (
                    <img
                      src={normalizeImageUrl(course.thumbnailUrl) ?? ""}
                      alt={course.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="font-medium">{course.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    Acceso de por vida
                  </p>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>
                    {formatCurrency(course.salePrice ?? 0, course.currency)}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span>
                    {formatCurrency(course.salePrice ?? 0, course.currency)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-semibold">Metodo de Pago</h2>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <CertificationDisclaimer variant="compact" />
              </div>
              <div className="mb-4">
                <TermsCheckbox
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
              </div>
              {PAYPAL_CLIENT_ID ? (
                <PayPalScriptProvider
                  options={{
                    clientId: PAYPAL_CLIENT_ID,
                    currency: course.currency,
                  }}
                >
                  <PayPalButtons
                    style={{ layout: "vertical", shape: "rect" }}
                    disabled={
                      !acceptedTerms ||
                      createOrderMutation.isPending ||
                      captureOrderMutation.isPending
                    }
                    createOrder={async () => {
                      if (!acceptedTerms) {
                        toast.error(
                          "Debes aceptar los Términos y la Política de Privacidad",
                        );
                        throw new Error("terms_not_accepted");
                      }
                      const result = await createOrderMutation.mutateAsync();
                      return result.orderId;
                    }}
                    onApprove={async (data) => {
                      await captureOrderMutation.mutateAsync(data.orderID);
                    }}
                    onError={(err) => {
                      console.error("PayPal error:", err);
                      toast.error("Error con PayPal. Intenta de nuevo.");
                    }}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>PayPal no esta configurado.</p>
                  <p className="text-sm mt-2">
                    Contacta al administrador para habilitar pagos.
                  </p>
                </div>
              )}

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Shield className="h-4 w-4" />
                <span>Pago seguro con PayPal</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-lg p-8 text-center max-w-sm mx-4">
            <Spinner size="lg" className="mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Procesando tu pago</h3>
            <p className="text-sm text-muted-foreground">
              Por favor no cierres esta ventana mientras completamos tu compra.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
